import { FormField } from "@/types";
import { UseFormRegister, UseFormStateReturn, FieldValues, Path, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { get } from "lodash";
import { JSX } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props<T extends FieldValues> = {
  className?: string;
  field: FormField;
  errors: UseFormStateReturn<T>["errors"];
  register: UseFormRegister<T>;
  watch?: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
};

/**
 * FormSelect component for rendering a select dropdown in a form using shadcn select components.
 * Supports rendering options from the provided input field configuration.
 *
 * @template T - The schema type, such as TLoginSchema or TCreateUserSchema.
 * @param {Props<T>} props - The props object.
 * @param {InputGroup} props.field - The input field configuration, including options.
 * @param {UseFormStateReturn<T>["errors"]} props.errors - The form errors.
 * @param {UseFormRegister<T>} props.register - The register function from react-hook-form.
 * @param {UseFormSetValue<T>} props.setValue - The setValue function from react-hook-form.
 *
 * @returns {JSX.Element} The rendered form select component.
 *
 * @example
 * ```tsx
 * import { useForm } from "react-hook-form";
 * import { TCreateUserSchema, CreateUserSchema } from "@/schemas/CreateUserSchema";
 * import FormSelect from "@/components/FormSelect";
 *
 * const CreateUserForm = () => {
 *   const { register, handleSubmit, setValue, formState: { errors } } = useForm<TCreateUserSchema>({
 *     resolver: zodResolver(CreateUserSchema),
 *   });
 *
 *   return (
 *     <form onSubmit={handleSubmit((data) => console.log(data))}>
 *       <FormSelect
 *         field={{ name: "role", title: "Role", dropdownOptions: [
 *           { text: "User", value: "USER" },
 *           { text: "Admin", value: "ADMIN" },
 *           { text: "Super User", value: "SUPERUSER" }
 *         ], required: true }}
 *         errors={errors}
 *         register={register}
 *         setValue={setValue}
 *       />
 *     </form>
 *   );
 * };
 * ```
 */
const FormSelect = <T extends FieldValues>({ field, errors, register, watch, setValue }: Props<T>): JSX.Element => {

  // Use lodash's get to retrieve nested errors safely
  const fieldError = get(errors, field.name as Path<T>);

  // Register the field with react-hook-form
  const { onChange: _onChange, ...registerProps } = register(field.name as Path<T>, { required: field.required });

  const handleValueChange = (value: string) => {
    setValue(field.name as Path<T>, value as any, { shouldValidate: true });
  };

  return (
    <div key={`select-input-${field.name}`} className="relative z-10">
      {/* Select label */}
      <Label htmlFor={field.name} className={`select-none mb-2 dark:text-gray-200 ${fieldError && "text-red-500"}`}>
        {field.title}
      </Label>

      {/* Select field */}
      <Select
        value={String(watch && watch(field.name as Path<T>) || field.defaultValue || "")}
        onValueChange={handleValueChange}
        disabled={field.disabled}
      >
        <SelectTrigger
          className={`${field.className} ${fieldError && "border-red-500"} dark:placeholder:text-gray-400 dark:text-gray-200
                      dark:disabled:text-gray-400 dark:disabled:placeholder:text-gray-400 bg-transparent dark:bg-transparent
                      text-gray-800 dark:focus:ring-mforange focus:ring-mfpurple h-[36px] w-full`}
          data-cy={`${field.name}-select`}
          {...registerProps}
        >
          <SelectValue placeholder={field.placeholder} />
        </SelectTrigger>
        <SelectContent className="dark:bg-mfdarklight max-h-[300px] overflow-y-auto">
          {field.dropdownOptions?.map((option) => (
            <SelectItem key={`${field.name}-select-option-${option.value}`} className="dark:hover:bg-mforange hover:bg-mforange" value={String(option.value)}>
              {option.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Error message */}
      {fieldError && (
        <p className="text-sm font-medium text-red-500 dark:text-red-500">
          {fieldError?.message as string}
        </p>
      )}
    </div>
  );
};

export default FormSelect;
